import { datadogLogs, StatusType } from '@datadog/browser-logs';
import type { GraphQLError } from 'graphql';

import { getSessionId } from '../../../utils';

import { beforeSend } from './beforeSend';
import { scrubSensitiveData } from './utils';

export type InputProps = Record<string, any>;

const {
  DATADOG_BROWSER_LOG_ENABLED: isBrowserLogEnabled,
  BRAND: brand,
  COUNTRY: country,
  SEEK_ENV: env,
  VERSION: version,
  SITE: site,
} = (typeof window !== 'undefined' && window.MY_JOBS_CONFIG) || {};

let candidateId = '';

export const initBrowserLog = (globalTags: string[]): void => {
  if (!isBrowserLogEnabled) {
    return;
  }
  // for more info about the configuration:
  // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
  datadogLogs.init({
    service: 'ca-myjobs-ui',
    clientToken: 'pub48765f65cb6a43a983afa07ef9392d65',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env,
    version,
    beforeSend,
  });

  datadogLogs.setGlobalContext({
    brand,
    country,
    site,
    ...globalTags.reduce((acc, tag) => {
      const [key, value] = tag.split(':');
      return {
        ...acc,
        [key]: value,
      };
    }, {}),
  });
};

export const setCandidateId = (id: string) => {
  candidateId = id;
};

const getExtrasDetails = (extras: any, error?: BrowserLogError) => ({
  'seek-sessionID': getSessionId(),
  candidateId,
  ...scrubSensitiveData(error as InputProps),
  ...scrubSensitiveData(extras),
});

const log = (
  level: StatusType,
  error: BrowserLogError,
  extras: any = {},
): void => {
  if (!isBrowserLogEnabled) {
    return;
  }

  const extrasDetail = getExtrasDetails(extras, error);

  switch (level) {
    case 'error': {
      datadogLogs.logger.error(
        error.message,
        {
          'error.kind': error.type,
          'error.stack': Boolean(error.stack) && error.stack,
          ...extrasDetail,
        },
        {
          ...error,
          name: error.type,
          stack: error.stack,
          message: error.message,
        },
      );
      break;
    }
    case 'warn': {
      datadogLogs.logger.warn(error.message, {
        'error.kind': error.type,
        'error.stack': Boolean(error.stack) && error.stack,
        ...extrasDetail,
      });
      break;
    }
    case 'info': {
      datadogLogs.logger.info(error.message, {
        ...extrasDetail,
      });
      break;
    }
    case 'debug': {
      datadogLogs.logger.debug(error.message, {
        'error.kind': error.type,
        'error.stack': Boolean(error.stack) && error.stack,
        ...extrasDetail,
      });
      break;
    }
    default: {
      break;
    }
  }
};

export interface BrowserLogError {
  type: string;
  message: string;
  stack?: string;
  graphQLErrors?: readonly GraphQLError[];
}

export const logError = (error: BrowserLogError, extras: any = {}): void => {
  log(StatusType.error, error, extras);
};

export const logWarn = (error: BrowserLogError, extras: any = {}): void => {
  log(StatusType.warn, error, extras);
};

export const logDebug = (
  message: string,
  type: string,
  extras: any = {},
): void => {
  log(
    StatusType.debug,
    {
      message,
      type,
    },
    extras,
  );
};

export const logInfo = (
  message: string,
  type: string,
  extras: any = {},
): void => {
  log(
    StatusType.info,
    {
      message,
      type,
    },
    extras,
  );
};
